<template>
  <div class="scrollbar-none mt-20 flex items-center gap-8 overflow-x-auto">
    <template v-if="loading">
      <RevSkeleton
        v-for="(_, index) in placeholderChipCount"
        :key="index"
        class="rounded-full shrink-0"
        height="32px"
        shape="rectangle"
        width="80px"
      />
    </template>
    <template v-else>
      <RevChip
        v-for="facet in facets"
        :key="facet.name"
        :class="chipClass((filters[facet.name] ?? []).length > 0)"
        has-dropdown
        :label="facetLabel(facet)"
        :model-value="facetChipValue(facet)"
        size="small"
        @click="openFacet($event, facet)"
      />

      <RevChip
        v-if="priceFacet"
        :class="chipClass(price !== undefined)"
        has-dropdown
        :label="facetLabel(priceFacet)"
        :model-value="priceChipValue"
        size="small"
        @click="openFacet($event, priceFacet)"
      />
    </template>
  </div>

  <RevDrawer
    class="mood-inverse bottom-0 top-auto h-auto max-h-[80vh] rounded-t-lg bg-[#191c20] text-static-default-hi"
    :name="MODAL_NAME"
    :title="selectedFacet ? facetLabel(selectedFacet) : ''"
  >
    <template #beforeTitle>
      <RevButtonIcon
        :alternative-text="i18n(dsTranslations.buttonClose)"
        :icon="IconCross"
        variant="ghost"
        @click="close"
      />
    </template>
    <template #afterTitle>
      <div class="h-40 flex items-center">
        <OutletButton
          v-if="canErase"
          size="small"
          variant="primary"
          @click="erase"
        >
          {{ i18n(translations.clear) }}
        </OutletButton>
      </div>
    </template>

    <template #body>
      <section
        v-if="selectedFacet && isPriceFacet(selectedFacet)"
        class="flex flex-col gap-24 py-16"
      >
        <div class="flex items-baseline justify-between">
          <span class="body-1">{{ formatPrice(priceRange[0]) }}</span>
          <span class="body-1">{{ formatPrice(priceRange[1]) }}</span>
        </div>

        <RevRange
          v-model="priceRange"
          :label="[i18n(translations.rangeMin), i18n(translations.rangeMax)]"
          :max="selectedFacet.max"
          :min="0"
          :step="selectedFacet.step || 1"
        />
      </section>

      <ul v-else-if="selectedFacet" class="flex flex-col gap-16 py-16">
        <li v-for="value in selectedFacet.values" :key="value.name">
          <RevCheckbox
            :id="`outlet-${selectedFacet.name}-${value.name}`"
            v-model="filters[selectedFacet.name]"
            :value="value.name"
            variant="full"
          >
            <template #label>{{ value.label }}</template>
          </RevCheckbox>
        </li>
      </ul>
    </template>

    <template #footer>
      <OutletButton class="w-full mt-12 mb-20" variant="primary" @click="close">
        {{ i18n(translations.seeResults, { count: searchNbResults }) }}
      </OutletButton>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type {
  ConfigurationFacet,
  Facet,
  UiPriceFacet,
} from '@backmarket/nuxt-layer-recommendation/filtering/types'
import { useFilterTracking } from '@backmarket/nuxt-layer-recommendation/filtering/useFilterTracking'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevChip } from '@ds/components/Chip'
import { RevDrawer } from '@ds/components/Drawer'
import { closeModal, openModal } from '@ds/components/ModalBase'
import { RevRange } from '@ds/components/Range'
import { RevSkeleton } from '@ds/components/Skeleton'
import { IconCross } from '@ds/icons/IconCross'
import { dsTranslations } from '@ds/translations'

import OutletButton from '../../../../components/OutletButton/OutletButton.vue'

import translations from './OutletFilterBar.translations'

const MODAL_NAME = 'outlet-filter-drawer'

// Facet name → translation. Keeps the chip + drawer title language-aware
// while the shared `ConfigurationFacet.title` keeps its English string for
// the (untranslated) layer-recommendation utilities.
const FACET_TRANSLATIONS: Record<string, (typeof translations)['facetPrice']> =
  {
    price: translations.facetPrice,
    backbox_grade: translations.facetCondition,
    model: translations.facetModel,
    year_date_release: translations.facetReleaseYear,
    storage: translations.facetStorage,
    screen_size: translations.facetScreenSize,
    color: translations.facetColor,
    memory: translations.facetMemory,
    processor_type: translations.facetProcessor,
    keyboard_type_language: translations.facetKeyboardLanguage,
  }

const props = defineProps<{
  facets: Facet[]
  priceFacet: UiPriceFacet | undefined
  searchNbResults: number
  // Initial-load mode: swap chips for skeletons so the bar isn't empty before
  // Algolia comes back with facet values.
  loading?: boolean
  // Used during `loading` to size the skeleton row to the same chip count we
  // expect to render once data lands — keeps width stable between states.
  placeholderFacets?: ConfigurationFacet[]
}>()

const filters = defineModel<Record<string, string[]>>('filters', {
  required: true,
})
const price = defineModel<[number, number] | undefined>('price', {
  required: true,
})

const i18n = useI18n()
const { defaultCurrency: currency } = useMarketplace().market
const { trackFilterClick } = useFilterTracking()

const selectedFacet = ref<Facet | UiPriceFacet | null>(null)

function isPriceFacet(facet: Facet | UiPriceFacet): facet is UiPriceFacet {
  return facet.name === 'price'
}

function facetLabel(facet: Facet | UiPriceFacet): string {
  const translation = FACET_TRANSLATIONS[facet.name]

  return translation ? i18n(translation) : facet.title
}

// RevChip's default styling fills the chip with a coloured background; for
// the outlet bar we want a flat bordered look on inactive chips, but keep
// the filled pressed state when a chip carries a value (otherwise the
// pressed text colour leaves the label invisible on a transparent bg).
function chipClass(isActive: boolean) {
  if (isActive) return 'shrink-0 mood-main !bg-static-brand-hi'

  return '!bg-transparent shrink-0 hover:!bg-action-default-mid-hover focus-visible:!bg-transparent !border-action-default-low'
}

// One skeleton per expected chip (price + each config facet) so the row
// occupies the same footprint before and after data lands.
const placeholderChipCount = computed(
  () => (props.placeholderFacets?.length ?? 0) + 1,
)

const priceChipValue = computed<string[] | boolean>(() => {
  if (!price.value) return false

  const min = i18n.price(
    { amount: price.value[0].toString(), currency },
    { maximumFractionDigits: 0 },
  )
  const max = i18n.price(
    { amount: price.value[1].toString(), currency },
    { maximumFractionDigits: 0 },
  )

  return [`${min} – ${max}`]
})

function facetChipValue(facet: Facet): string[] | boolean {
  const selected = filters.value[facet.name] ?? []
  if (selected.length === 0) return false

  return selected
    .map((name) => facet.values.find((v) => v.name === name)?.label)
    .filter((label): label is string => Boolean(label))
}

function openFacet(event: Event, facet: Facet | UiPriceFacet) {
  selectedFacet.value = facet
  openModal(MODAL_NAME, event)
  trackFilterClick({
    zone: 'filter',
    name: `open_${facet.name}`,
  })
}

function close() {
  trackFilterClick({
    zone: 'filter',
    name: 'see_results',
  })
  closeModal(MODAL_NAME)
}

// Local working copy of the price range. Defaults to the full span so the
// `RevRange` slider has thumbs to drag before the user has interacted with it.
const priceRange = ref<[number, number]>(
  price.value ?? [0, props.priceFacet?.max ?? 0],
)

watch(
  () => props.priceFacet?.max,
  (max) => {
    if (max !== undefined && !price.value) {
      priceRange.value = [0, max]
    }
  },
)

watch(price, (value) => {
  priceRange.value = value ?? [0, props.priceFacet?.max ?? 0]
})

// Track filter changes (for standard facets like condition, model, etc.)
watch(
  filters,
  (newFilters, oldFilters) => {
    // Find which facet changed
    for (const [facetName, newValues] of Object.entries(newFilters)) {
      const oldValues = oldFilters[facetName] ?? []
      if (
        newValues.length !== oldValues.length ||
        !newValues.every((v) => oldValues.includes(v))
      ) {
        // Determine if a value was added or removed
        const addedValues = newValues.filter((v) => !oldValues.includes(v))
        const removedValues = oldValues.filter((v) => !newValues.includes(v))
        const changedValue = addedValues[0] ?? removedValues[0]

        // Find the facet to get the label
        const facet = props.facets.find((f) => f.name === facetName)
        const valueLabel = facet?.values.find(
          (v) => v.name === changedValue,
        )?.label

        trackFilterClick({
          zone: 'filter',
          name: facetName,
          value: valueLabel ?? changedValue ?? 'All',
        })
      }
    }
  },
  { deep: true },
)

// Only treat the slider as an active filter once the user has moved away from
// the full range. An undefined model value keeps `numericFilters` out of the
// Algolia request entirely.
watch(priceRange, (value) => {
  const max = props.priceFacet?.max ?? 0
  const isFullRange = value[0] === 0 && value[1] === max

  price.value = isFullRange ? undefined : value
})

// Track price range changes
watch(priceRange, (newInput, oldInput) => {
  if (newInput[0] !== oldInput[0]) {
    trackFilterClick({
      name: 'minSlider',
      zone: 'priceFilter',
      value: newInput[0],
    })
  }
  if (newInput[1] !== oldInput[1]) {
    trackFilterClick({
      name: 'maxSlider',
      zone: 'priceFilter',
      value: newInput[1],
    })
  }
})

function formatPrice(amount: number) {
  return i18n.price({ amount: amount.toString(), currency })
}

const canErase = computed(() => {
  if (!selectedFacet.value) return false
  if (isPriceFacet(selectedFacet.value)) return price.value !== undefined

  return (filters.value[selectedFacet.value.name] ?? []).length > 0
})

function erase() {
  if (!selectedFacet.value) return

  if (isPriceFacet(selectedFacet.value)) {
    price.value = undefined
    trackFilterClick({
      zone: 'priceFilter',
      name: 'clear',
    })
  } else {
    filters.value[selectedFacet.value.name] = []
    trackFilterClick({
      zone: 'filter',
      name: `clear_${selectedFacet.value.name}`,
    })
  }
}
</script>
